import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTA from "@/components/CTA";
import Seo, { serviceSchema } from "@/components/Seo";
import { CaretRight, Check } from "@phosphor-icons/react";
import { trackViewService } from "@/lib/analytics";

export function ServicesIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="services-index">
      <Seo title="Solar Surveillance Services — Nevada & Utah" description="Solar surveillance trailer rentals, mobile security trailer deployments, pole-mounted solar systems, remote video monitoring and rapid-deployment security across Nevada and Utah." path="/services" />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Solutions" }]} /></section>
      <section className="container-x pb-12">
        <div className="eyebrow text-solar mb-3">Solutions</div>
        <h1 className="mb-4">What we deploy — and how.</h1>
        <p className="text-white/75 text-lg max-w-2xl">Every solution below is delivered using one or more of our three system configurations.</p>
      </section>
      <section className="container-x pb-20 grid md:grid-cols-2 gap-px bg-chrome border border-chrome">
        {items.map((s) => (
          <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="bg-obsidian p-8 hover:bg-steel transition-colors group">
            <div className="font-display uppercase text-2xl font-bold mb-3 group-hover:text-solar transition-colors">{s.title}</div>
            <p className="text-white/70 mb-4 leading-relaxed">{s.summary}</p>
            <span className="text-solar text-sm font-bold uppercase tracking-wider flex items-center gap-1">Explore <CaretRight size={12} weight="bold" /></span>
          </Link>
        ))}
      </section>
      <CTA />
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const [svc, setSvc] = useState(null);
  const [systems, setSystems] = useState([]);
  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => {
      setSvc(r.data);
      trackViewService(r.data);
    }).catch(() => setSvc(false));
    api.get("/systems").then((r) => setSystems(r.data));
  }, [slug]);
  if (svc === false) return <div className="container-x py-20"><h1>Service not found</h1></div>;
  if (!svc) return <div className="container-x py-20 text-white/60">Loading…</div>;

  const relatedSystems = systems.filter((s) => (svc.systems || []).includes(s.slug));

  return (
    <div data-testid={`service-detail-${slug}`}>
      <Seo
        title={svc.title}
        description={svc.summary}
        path={`/services/${slug}`}
        schema={serviceSchema(svc)}
        breadcrumb={[{ label: "Home", path: "/" }, { label: "Solutions", path: "/services" }, { label: svc.title }]}
      />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Solutions", to: "/services" }, { label: svc.title }]} /></section>
      <section className="container-x pb-14">
        <div className="eyebrow text-solar mb-3">Solution</div>
        <h1 className="mb-5 max-w-4xl">{svc.title}</h1>
        <p className="text-white/80 text-lg leading-relaxed max-w-3xl mb-6">{svc.summary}</p>
        <Link to="/request-a-quote" data-testid="service-quote-cta" className="btn-solar inline-block">Request Assessment</Link>
      </section>

      <section className="bg-steel border-y border-chrome py-16">
        <div className="container-x grid lg:grid-cols-2 gap-10">
          <div>
            <div className="eyebrow text-solar mb-3">The Problem</div>
            <p className="text-white/85 text-lg leading-relaxed">{svc.problem}</p>
          </div>
          <div>
            <div className="eyebrow text-solar mb-3">How It Works</div>
            <p className="text-white/85 text-lg leading-relaxed">{svc.how_it_works}</p>
          </div>
        </div>
      </section>

      {relatedSystems.length > 0 && (
        <section className="container-x py-16">
          <div className="eyebrow text-solar mb-3">Recommended Systems</div>
          <h2 className="mb-8">Configurations we deploy for this service.</h2>
          <div className="grid md:grid-cols-3 gap-px bg-chrome border border-chrome">
            {relatedSystems.map((sys) => (
              <Link key={sys.slug} to={`/systems/${sys.slug}`} className="bg-obsidian p-6 hover:bg-steel transition-colors">
                <div className="eyebrow text-solar mb-2">{sys.name}</div>
                <div className="font-display uppercase text-xl font-bold mb-2">{sys.short_name}</div>
                <div className="text-white/70 text-sm mb-4 leading-relaxed">{sys.summary?.substring(0, 120)}…</div>
                <span className="text-solar text-sm font-bold uppercase tracking-wider flex items-center gap-1">View <CaretRight size={12} weight="bold" /></span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <CTA />
    </div>
  );
}
